import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Divider, Paper, type Theme } from '@mui/material';
import type {
  DataGridPremiumProps,
  FilterColumnsArgs,
  GridColDef,
  GridFilterItem,
  GridRowId,
  GridToolbarProps,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  gridColumnLookupSelector,
  gridFilterModelSelector,
  GridLogicOperator,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiContext,
  useGridSelector,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import {
  type JSX,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import type { QuickFilterProps } from '~/components/common/QuickFilterInput';
import { QuickFilterInput } from '~/components/common/QuickFilterInput';
import { BUTTON_SX } from '~/theme';

import type { LiftAIGridColDef } from './colDefTemplate';
import { EXPORT_BUTTON_SLOT_PROPS } from './exportButtonSlotProps';
import { activeFilterItems, sameFilterItems } from './helpers';
import type { GridViewsControlsProps } from './views/GridViewsControls';
import { GridViewsControls } from './views/GridViewsControls';

// These are banned for external usage of LiftAITable
type BannedProps =
  | 'autoHeight' // This is deprecated by MUI so we're not allowing it
  | 'autoPageSize'
  | 'pagination'
  | 'sx'
  | 'slots';

/**
 * Props for LiftAITable component
 * @param rows: Array of rows to be displayed in the table
 * @param columns: Array of columns to be displayed in the table
 * @param tableHeaderTitle: Title of the table
 */
interface LiftAITableProps<TRow extends GridValidRowModel> extends Omit<
  DataGridPremiumProps<TRow>,
  BannedProps
> {
  columns: LiftAIGridColDef<TRow>[];
  tableHeaderTitle: string;
  enableQuickFilter?: boolean;
  quickFilterPlaceHolder?: string;
  /**
   * Search state owned by the page, for a search that feeds a server-side query.
   * Implies the search box. Omit and the box drives the grid's own filter model.
   */
  quickFilterProps?: QuickFilterProps;
  toolbarProps?: ExternalLiftAIToolbarProps;
  /** Content inside the table's surface, above the toolbar — for a control acting on the table as a whole rather than on a row. */
  headerContent?: ReactNode;
  /**
   * Where a row leads: a click anywhere non-interactive in it opens that URL in a new
   * tab. Takes precedence over `onRowClick` and over click-to-expand.
   */
  rowHref?: (row: TRow) => string | undefined;
  onRowHover?: (rowId: GridRowId | null) => void;
  highlightedRowId?: GridRowId | null;
  paginationModel?: { pageSize: number; page: number };
  pageSizeOptions?: number[];
  // When set, the grid is bounded to this height and scrolls internally — its
  // horizontal scrollbar stays in view — instead of growing to fit its rows and
  // making the whole page scroll. Opt-in; when unset the grid keeps the
  // flex-grow behavior the other tables rely on.
  height?: number | string;
  // Floor applied on both paths — the flex-grow path directly, and as a CSS
  // `minHeight` alongside a bounded `height` so a too-small computed height
  // (e.g. a crushed viewport measurement) can't collapse the grid below this.
  // Opt-in for a table whose flex siblings, or whose height calculation, can
  // otherwise crush it below a usable size.
  minHeight?: number | string;

  // Investigate why this is needed
  apiRef: MutableRefObject<GridApiPremium>;
}

type ExternalLiftAIToolbarProps = {
  /**
   * Pass `CustomGridToolbarExport` to override the export tool of the table.
   * This will replace the component entirely, allowing for fully customize UI and behavior.
   */
  CustomGridToolbarExport?: () => JSX.Element;

  /**
   * Optional actions rendered next to the quick filter (right side of toolbar).
   */
  CustomToolbarActions?: () => JSX.Element;

  /** Drops the density selector, for a grid whose rows size themselves. */
  hideDensity?: boolean;

  filterControls?: ReactNode;

  /** Saved views. Supplied by `useGridViews`; absent means the table has none. */
  views?: GridViewsControlsProps;

  /**
   * Puts the filters back to the active view's. Supplied by `useGridViews`; without it Clear
   * Filters empties them, which is right for a table with no views to go back to.
   */
  resetFilters?: () => void;

  /**
   * Drops the page's filter query params. Only for a page that reads them again when it
   * fetches, where emptying the grid's model alone leaves the query narrowed regardless.
   */
  onClearUrlFilters?: () => void;

  /**
   * Filter items the page derives from the global filter bar. They are not the user's
   * table filters, so they neither light up Clear Filters nor get cleared by it.
   */
  globalFilterItemIds?: readonly string[];

  /**
   * Rendered under the toolbar, for a narrowing the page applied that no column can show.
   * It sits with the filter controls because that is where a reader looks to find out why
   * the list is short.
   */
  scopeContent?: ReactNode;

  beforeSearch?: ReactNode;
};

export type { ExternalLiftAIToolbarProps as LiftAIToolbarProps };

// Overridden rather than inherited: MUI's is the prop bag for its own
// `GridToolbarQuickFilter`, which this toolbar no longer renders.
type LiftAIToolbarProps = Omit<GridToolbarProps, 'quickFilterProps'> &
  ExternalLiftAIToolbarProps & {
    onClearFilters: () => void;
    quickFilterProps?: Partial<QuickFilterProps>;
  };

// Teaches MUI that `slotProps.toolbar` carries this toolbar's own props, so nothing
// needs a per-render wrapper component between the grid and it.
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onClearFilters: () => void;
    quickFilterProps?: Partial<QuickFilterProps>;
    CustomGridToolbarExport?: () => JSX.Element;
    CustomToolbarActions?: () => JSX.Element;
    hideDensity?: boolean;
    filterControls?: ReactNode;
    views?: GridViewsControlsProps;
    resetFilters?: () => void;
    onClearUrlFilters?: () => void;
    globalFilterItemIds?: readonly string[];
    scopeContent?: ReactNode;
    beforeSearch?: ReactNode;
  }
}

/** Drives the search box off the grid's own filter model, for the nested tables whose page owns no search state. */
function useGridQuickFilter(placeholder?: string): QuickFilterProps {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const handleSearchValueChange = useCallback(
    (value: string) => {
      apiRef.current.setQuickFilterValues(value ? value.split(' ') : []);
    },
    [apiRef],
  );

  return {
    searchValue: filterModel?.quickFilterValues?.join(' ') ?? '',
    handleSearchValueChange,
    placeholder,
  };
}

function LiftAIToolbar({
  showQuickFilter,
  onClearFilters,
  CustomGridToolbarExport,
  CustomToolbarActions,
  hideDensity = false,
  filterControls,
  quickFilterProps,
  views,
  globalFilterItemIds,
  scopeContent,
  beforeSearch,
}: LiftAIToolbarProps) {
  const apiRef = useGridApiContext();
  // Through the grid's selector rather than `apiRef.current.state`: reading the ref
  // during render is a lint error, and left Clear Filters visible after the last
  // filter was removed.
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const gridQuickFilter = useGridQuickFilter(quickFilterProps?.placeholder);

  const ownedSearch = quickFilterProps?.handleSearchValueChange;
  const search: QuickFilterProps | null = !showQuickFilter
    ? null
    : ownedSearch
      ? {
          searchValue: quickFilterProps?.searchValue ?? '',
          handleSearchValueChange: ownedSearch,
          placeholder: quickFilterProps?.placeholder,
          clearToken: quickFilterProps?.clearToken,
        }
      : { ...gridQuickFilter, clearToken: quickFilterProps?.clearToken };

  // Only the items that actually narrow the table, and never the page's own; see
  // `activeFilterItems`.
  const ownActiveItems = (items: GridFilterItem[] = []) =>
    activeFilterItems(
      columnLookup,
      items.filter((item) => !globalFilterItemIds?.includes(String(item.id))),
    );
  const userFilterItems = ownActiveItems(filterModel?.items);

  // What Clear Filters would restore. On a table with views the button goes back to the
  // active view rather than to nothing, so it has something to do only when the filters on
  // screen differ from that view's — otherwise it sat there permanently doing nothing. The
  // page's items come off this side too: a view saved before they were kept out still holds
  // them, and Clear Filters never puts them back, so they would keep the button up for good.
  const activeView = views?.views.find((view) => view.id === views.activeViewId);
  const viewFilterItems = ownActiveItems(activeView?.state.filter?.filterModel?.items);
  const filtersDifferFromView = views
    ? !sameFilterItems(userFilterItems, viewFilterItems)
    : userFilterItems.length > 0;
  // A blank search term is not a filter. Counting the values alone kept the button up
  // after the search box's X emptied it, and a `['']` persisted to session storage
  // would outlive the fix. Read off the resolved search, since a page-owned term never
  // reaches the grid's model.
  const showClearButton =
    Boolean(search?.searchValue.trim()) ||
    filtersDifferFromView ||
    Boolean(filterModel?.quickFilterValues?.some((value) => value.trim() !== ''));

  return (
    <Box sx={{ pb: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
      >
        <GridToolbarContainer sx={{ flexWrap: 'wrap', gap: 1 }}>
          {/* Two groups, divided: which view you are on, then the controls that shape it —
            columns, density, then filters. */}
          {views ? <GridViewsControls {...views} /> : null}
          {views ? <Divider orientation="vertical" flexItem /> : null}
          <GridToolbarColumnsButton />
          {hideDensity ? null : <GridToolbarDensitySelector />}
          <GridToolbarFilterButton />
          {showClearButton ? (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={onClearFilters}
              variant="outlined"
              sx={BUTTON_SX}
            >
              Clear Filters
            </Button>
          ) : null}
          {filterControls ? (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              {filterControls}
            </>
          ) : null}
        </GridToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Search first, then the page's own action, then export: the action is the
              one button here that changes data, so it sits between the two controls
              that only read it rather than leading the group. */}
          {beforeSearch}
          {search ? <QuickFilterInput {...search} /> : null}
          {CustomToolbarActions ? <CustomToolbarActions /> : null}
          {CustomGridToolbarExport ? (
            <CustomGridToolbarExport />
          ) : (
            <GridToolbarExport slotProps={EXPORT_BUTTON_SLOT_PROPS} />
          )}
        </Box>
      </Box>
      {/* Its own row: it is not one of the controls, and beside them it would be squeezed
          out when the toolbar wraps. */}
      {scopeContent ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, px: 0.5 }}>
          {scopeContent}
        </Box>
      ) : null}
    </Box>
  );
}

const PINNED_CELLS = '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight';

const getHighlightedRowStyles = (rowId: GridRowId) => {
  const highlighted = `.MuiDataGrid-row[data-id="${String(rowId)}"]`;
  return {
    '& .MuiDataGrid-row:hover': { backgroundColor: 'transparent' },
    [`& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row:hover :is(${PINNED_CELLS})`]: {
      backgroundColor: 'var(--DataGrid-pinnedBackground)',
    },
    [`& ${highlighted}`]: { backgroundColor: 'action.hover' },
    [`& .MuiDataGrid-virtualScrollerContent ${highlighted} :is(${PINNED_CELLS})`]: {
      backgroundColor: 'var(--DataGrid-pinnedBackground)',
      backgroundImage: (theme: Theme) =>
        `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
    },
  };
};

/**
 * Wrapper around DataGridPremium component to facilitate
 * most of the Pro features. Such as: Quick filtering of columns,
 * column reordering, column pinning, etc.
 * @param props: LiftAITableProps
 * @returns DataGridPremium component
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function LiftAITable<TRow extends GridValidRowModel = any>({
  rows,
  columns,
  enableQuickFilter,
  quickFilterPlaceHolder,
  quickFilterProps,
  initialState,
  filterModel,
  onFilterModelChange,
  apiRef,
  toolbarProps: liftAIToolbarProps,
  headerContent,
  rowHref,
  onRowHover,
  highlightedRowId,
  paginationModel,
  pageSizeOptions = [10, 25, 50],
  height,
  minHeight,
  getDetailPanelContent,
  onRowClick,
  loading,
  // Premium's two heaviest column-menu entries, off unless a table asks for them.
  // Both compute over the rows the grid is holding, so on a server-paginated table
  // they silently answer for one page while reading as the whole set. Only Portfolio
  // and Compliance group; nothing aggregates.
  disableRowGrouping = true,
  disableAggregation = true,
  ...tableProps
}: LiftAITableProps<TRow>) {
  // A grid with nothing in it has nothing to scroll, so it grows to fit its empty
  // overlay rather than holding a screenful of blank. Still bounded while loading:
  // rows arrive empty first, and collapsing then would snap back a frame later.
  const boundedHeight = !loading && rows?.length === 0 ? undefined : height;

  const initialStateObj = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      ...initialState,
      pagination: paginationModel ? { paginationModel } : undefined,
    },
  });
  const pageOwnedSearchChange = quickFilterProps?.handleSearchValueChange;
  const keepFilterItemIds = liftAIToolbarProps?.globalFilterItemIds;
  const resetFiltersToView = liftAIToolbarProps?.resetFilters;
  const clearUrlFilters = liftAIToolbarProps?.onClearUrlFilters;
  // See `QuickFilterProps.clearToken`.
  const [clearToken, setClearToken] = useState(0);
  const onClearFilters = useCallback(() => {
    // Clearing must not widen the table past the global filter bar's own selection.
    const kept = keepFilterItemIds?.length
      ? gridFilterModelSelector(apiRef).items.filter((item) =>
          keepFilterItemIds.includes(String(item.id)),
        )
      : [];
    const cleared = {
      items: kept,
      quickFilterValues: [],
      quickFilterLogicOperator: GridLogicOperator.And,
      logicOperator: GridLogicOperator.And,
    };

    // A page-owned search term lives outside the grid's model, so emptying the model
    // alone would leave the table narrowed by the box the button had just cleared.
    pageOwnedSearchChange?.('');
    setClearToken((token) => token + 1);
    // Including in the URL: a page that re-reads it would put the filter back on next fetch.
    clearUrlFilters?.();

    // With views, "clear" means back to the view the reader is on, not back to nothing.
    if (resetFiltersToView) {
      resetFiltersToView();
      return;
    }

    if (onFilterModelChange) {
      onFilterModelChange(cleared, { reason: 'deleteFilterItem', api: apiRef.current });
      return;
    }

    // Otherwise the grid owns the model, as on Needs Attention. This used to return early, so the
    // button was there and pressing it left the table filtered.
    apiRef.current.setFilterModel(cleared, 'deleteFilterItem');
  }, [
    apiRef,
    onFilterModelChange,
    pageOwnedSearchChange,
    keepFilterItemIds,
    resetFiltersToView,
    clearUrlFilters,
  ]);

  // Everything that varies goes through `slotProps.toolbar`, never a wrapper component
  // built here: a `useCallback` closing over rebuilt props gave `slots.toolbar` a new
  // element type each render, and React remounts a subtree whose type changed — taking
  // the search box's caret and its uncommitted text with it.

  // A row with a detail panel expands on click anywhere in it, not just the chevron.
  // A caller that gave the row its own destination or click keeps that instead.
  const handleRowClick = useCallback<NonNullable<DataGridPremiumProps<TRow>['onRowClick']>>(
    (params, event, details) => {
      // Interactive content keeps its own behaviour, so the row doesn't act on top of a
      // cell's link or menu.
      const onInteractiveContent = Boolean(
        (event.target as HTMLElement | null)?.closest('a, button, input, [role="menuitem"]'),
      );

      if (rowHref) {
        if (onInteractiveContent) return;
        const href = rowHref(params.row);
        if (href) window.open(href, '_blank', 'noopener');
        return;
      }
      if (onRowClick) {
        onRowClick(params, event, details);
        return;
      }
      if (!getDetailPanelContent) return;
      if (onInteractiveContent) return;
      apiRef.current.toggleDetailPanel(params.id);
    },
    [rowHref, onRowClick, getDetailPanelContent, apiRef],
  );

  // `minWidth: 0` pairs with `maxWidth: '100%'`: as a flex item the Paper would
  // otherwise take its automatic minimum from the grid's columns and grow past its
  // container, moving the horizontal scrollbar off the grid and onto the page.
  return (
    <Paper
      sx={{
        padding: 1,
        minWidth: 0,
        maxWidth: '100%',
        // A definite height turns the grid into its own scroll container so its
        // horizontal scrollbar stays in view; otherwise keep the grow-to-fit
        // behavior the other tables rely on.
        ...(boundedHeight != null
          ? { height: boundedHeight, ...(minHeight != null ? { minHeight } : {}) }
          : { flex: '1 1 auto', ...(minHeight != null ? { minHeight } : {}) }),
      }}
    >
      {headerContent ? <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>{headerContent}</Box> : null}
      <DataGridPremium
        apiRef={apiRef}
        initialState={initialStateObj}
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        // Grow-to-fit only. `useGridDimensions` narrows the viewport by `scrollbarSize`
        // whenever `content.height > container.height`, and expanding a detail panel grows
        // the content a frame before the container follows — so every flex column briefly
        // re-lays-out 15px narrower and the rows jump sideways and back. A grid that grows
        // to fit never rests on a scrollbar, so measuring it as zero costs nothing. One
        // given a definite `height` really does scroll and keeps the true measurement.
        {...(boundedHeight == null ? { scrollbarSize: 0 } : {})}
        disableRowGrouping={disableRowGrouping}
        disableAggregation={disableAggregation}
        slots={{
          toolbar: LiftAIToolbar,
          // Chevrons rather than the grid's default +/−: a row that opens in place is a
          // disclosure. Set here because `slots` is closed to callers.
          detailPanelExpandIcon: ChevronRightIcon,
          detailPanelCollapseIcon: ExpandMoreIcon,
        }}
        slotProps={{
          row: onRowHover
            ? {
                onMouseEnter: (event: MouseEvent<HTMLElement>) =>
                  onRowHover(event.currentTarget.dataset.id ?? null),
                onMouseLeave: () => onRowHover(null),
              }
            : undefined,
          toolbar: {
            ...liftAIToolbarProps,
            onClearFilters,
            showQuickFilter: enableQuickFilter || Boolean(quickFilterProps),
            quickFilterProps: {
              ...quickFilterProps,
              placeholder: quickFilterProps?.placeholder ?? quickFilterPlaceHolder,
              clearToken,
            },
          },
          filterPanel: {
            // Force usage of "And" operator
            logicOperators: [GridLogicOperator.And],
            disableRemoveAllButton: true,
            sx: {
              // Enhanced responsive styles for filter panel
              '& .MuiDataGrid-filterForm': {
                '@media (max-width: 768px)': {
                  display: 'grid !important',
                  gridTemplateColumns: 'auto 1fr !important',
                  gap: '12px',
                  alignItems: 'start !important',
                  padding: '8px 0',
                  // First column: X button
                  '& .MuiDataGrid-filterFormDeleteIcon': {
                    gridColumn: '1',
                    gridRow: '1 / span 4',
                    alignSelf: 'center',
                  },
                  // Second column: Filter controls stacked
                  '& .MuiDataGrid-filterFormLogicOperatorInput': {
                    gridColumn: '2',
                    gridRow: '1',
                  },
                  '& .MuiDataGrid-filterFormColumnInput': {
                    gridColumn: '2',
                    gridRow: '2',
                  },
                  '& .MuiDataGrid-filterFormOperatorInput': {
                    gridColumn: '2',
                    gridRow: '3',
                  },
                  '& .MuiDataGrid-filterFormValueInput': {
                    gridColumn: '2',
                    gridRow: '4',
                  },
                  // Make all form controls full width with consistent height
                  '& > div:not(.MuiDataGrid-filterFormDeleteIcon)': {
                    width: '100% !important',
                    '& .MuiFormControl-root': {
                      width: '100% !important',
                    },
                    '& .MuiInputBase-root, & .MuiSelect-root': {
                      width: '100% !important',
                      minHeight: '40px !important',
                    },
                  },
                },
              },
              // Add backdrop for mobile to make modal-like experience
              '@media (max-width: 768px)': {
                '&::before': {
                  content: '""',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: -1,
                },
              },
            },
            filterFormProps: {
              /**
               * This is to remove the grouping column because it's duplicated
               *
               * Link: https://github.com/mui/mui-x/issues/12223
               */

              filterColumns: ({ columns }: FilterColumnsArgs) => {
                const filtered = columns.filter(
                  (column) => column.field !== '__row_group_by_columns_group__',
                );

                return filtered.map((column) => column.field);
              },
            },
          },
          columnsManagement: {
            getTogglableColumns: (columns: GridColDef[]) => {
              return columns
                .filter((column) => column.field !== '__row_group_by_columns_group__')
                .map((column) => column.field);
            },
          },
        }}
        sx={{
          border: 0,
          // Fill the definite-height Paper so the grid scrolls inside its own
          // box instead of growing the page.
          ...(boundedHeight != null ? { height: '100%' } : {}),
          '& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': {
            boxShadow: 'none',
          },
          ...(rowHref || onRowClick || getDetailPanelContent
            ? { '& .MuiDataGrid-row': { cursor: 'pointer' } }
            : {}),
          ...(highlightedRowId != null ? getHighlightedRowStyles(highlightedRowId) : {}),
          // Responsive filter panel styles
          '& .MuiDataGrid-panel': {
            '@media (max-width: 768px)': {
              position: 'fixed !important',
              top: '50% !important',
              left: '50% !important',
              transform: 'translate(-50%, -50%) !important',
              width: '90vw !important',
              maxWidth: 'none !important',
              height: 'auto !important',
              maxHeight: '80vh',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
              zIndex: 1300,
              '&::before': {
                content: '""',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: -1,
                pointerEvents: 'auto',
              },
            },
            '@media (max-width: 480px)': {
              width: '95vw !important',
              maxHeight: '85vh',
            },
          },
          '& .MuiDataGrid-panelHeader': {
            '@media (max-width: 768px)': {
              padding: '12px 16px',
              fontSize: '18px',
              borderBottom: '1px solid #e0e0e0',
            },
          },
          '& .MuiDataGrid-panelContent': {
            '@media (max-width: 768px)': {
              padding: '16px',
              maxHeight: '60vh',
              overflow: 'auto',
            },
          },
        }}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        getDetailPanelContent={getDetailPanelContent}
        onRowClick={handleRowClick}
        {...tableProps}
      />
    </Paper>
  );
}
